import {
  flexRender,
  type Row,
  type RowData,
  type Table as TanstackTable,
} from '@tanstack/react-table';
import { Loader2Icon } from 'lucide-react';
import type * as React from 'react';
import { Fragment, PropsWithChildren, useLayoutEffect, useRef, useState } from 'react';

import { Skeleton } from '@/components/core/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/core/table';
import { ColumnHeader } from '@/components/data-table/column-header';
import { cn } from '@/shared/helpers';

export type RowClickHandler<TData> = (
  row: Row<TData>,
  event: React.MouseEvent<HTMLTableRowElement>,
) => void;

type DataTableProps<TData extends RowData> = React.ComponentProps<'div'> & {
  table: TanstackTable<TData>;
  onClickRow?: RowClickHandler<TData>;
  getRowCanClick?: (row: Row<TData>) => boolean;
  renderExpandedRow?: (row: Row<TData>) => React.ReactNode;
  getRowClassName?: (row: Row<TData>) => string | undefined;
};

const defaultExpandedRowClassName = 'bg-muted/50 hover:!bg-muted/50';

function MessageRow<TData>({
  table,
  children,
}: PropsWithChildren<{ table: TanstackTable<TData> }>) {
  return (
    <TableRow className="hover:bg-inherit">
      <TableCell
        colSpan={table.getAllColumns().length}
        className="h-15 text-center text-muted-foreground"
      >
        {children}
      </TableCell>
    </TableRow>
  );
}

function renderRows<TData>(
  table: TanstackTable<TData>,
  onClickRow?: DataTableProps<TData>['onClickRow'],
  getRowCanClick?: DataTableProps<TData>['getRowCanClick'],
  renderExpandedRow?: DataTableProps<TData>['renderExpandedRow'],
  getRowClassName?: DataTableProps<TData>['getRowClassName'],
) {
  const { rows } = table.getRowModel();
  const { isLoading, hasError } = table.options.meta ?? {};

  // Show skeleton rows during initial load (no data available yet)
  if (isLoading) {
    const rowCount = table.getState().pagination?.pageSize || 10;
    return Array.from({ length: rowCount }).map((_, i) => (
      <TableRow key={i} className="hover:bg-transparent">
        {table.getAllColumns().map((col) => (
          <TableCell key={col.id}>
            {col.columnDef.meta?.loadingCell ?? <Skeleton className="h-5 w-full" />}
          </TableCell>
        ))}
      </TableRow>
    ));
  }
  if (hasError) {
    return <MessageRow table={table}>Error fetching results</MessageRow>;
  }
  if (!rows.length) {
    return <MessageRow table={table}>No results found</MessageRow>;
  }
  return rows.map((row, index) => {
    const nextRow = rows[index + 1];
    const canClickRow = getRowCanClick?.(row) ?? true;
    const rowClickHandler =
      onClickRow && canClickRow
        ? (event: React.MouseEvent<HTMLTableRowElement>) => onClickRow(row, event)
        : undefined;
    const rowClassName = getRowClassName?.(row);
    const rowExpandedClassName = getExpandedRowClassName(row);
    const expandedRowsAfter: Row<TData>[] = [];
    let expandedRow: Row<TData> | undefined = row;

    // Expanded content belongs after the whole visible subtree, not directly after
    // the parent row, so subrows can render between a parent and its details.
    while (expandedRow) {
      const subtreeEndsHere = !nextRow || nextRow.depth <= expandedRow.depth;
      if (renderExpandedRow && expandedRow.getIsExpanded() && subtreeEndsHere) {
        expandedRowsAfter.push(expandedRow);
      }
      expandedRow = expandedRow.getParentRow();
    }

    return (
      <Fragment key={row.id}>
        <TableRow
          data-state={row.getIsSelected() ? 'selected' : undefined}
          className={cn(rowExpandedClassName, rowClassName)}
          onClick={rowClickHandler}
        >
          {row.getVisibleCells().map((cell) => {
            const cellClassName = cell.column.columnDef.meta?.cellClassName?.(cell);
            const rowClickDisabled = cell.column.columnDef.meta?.enableRowClick === false;
            const truncate = cell.column.columnDef.meta?.truncate;
            const rawValue = cell.getValue();
            const truncateTitle = truncate && typeof rawValue === 'string' ? rawValue : undefined;

            return (
              <TableCell
                key={cell.id}
                className={cn(cellClassName, {
                  'text-left': cell.column.columnDef.meta?.align === 'left',
                  'text-right': cell.column.columnDef.meta?.align === 'right',
                  'text-center': cell.column.columnDef.meta?.align === 'center',
                  'cursor-pointer': !!rowClickHandler && !rowClickDisabled,
                })}
                onClick={rowClickDisabled ? (event) => event.stopPropagation() : undefined}
              >
                <div
                  className={cn('peer', truncate && 'max-w-90 min-w-full truncate')}
                  title={truncateTitle}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
                <span
                  className="select-none before:text-muted-foreground/50 peer-empty:before:content-['–']"
                  aria-hidden
                />
              </TableCell>
            );
          })}
        </TableRow>
        {expandedRowsAfter.map((expandedRow) => (
          <TableRow
            key={`${expandedRow.id}-expanded`}
            className={cn(
              'hover:bg-inherit',
              getExpandedRowClassName(expandedRow),
              getRowClassName?.(expandedRow),
            )}
          >
            <TableCell colSpan={expandedRow.getVisibleCells().length} className="whitespace-normal">
              {renderExpandedRow?.(expandedRow)}
            </TableCell>
          </TableRow>
        ))}
      </Fragment>
    );
  });
}

function getExpandedRowClassName<TData>(row: Row<TData>) {
  if (row.getIsExpanded()) return defaultExpandedRowClassName;

  let parentRow = row.getParentRow();
  while (parentRow) {
    if (parentRow.getIsExpanded()) return defaultExpandedRowClassName;
    parentRow = parentRow.getParentRow();
  }

  return undefined;
}

export function DataTable<TData>({
  table,
  children,
  className,
  style,
  onClickRow,
  getRowCanClick,
  renderExpandedRow,
  getRowClassName,
  ...props
}: DataTableProps<TData>) {
  const { isLoadingWithStaleData, isLoading } = table.options.meta ?? {};
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);

  const update = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const tableElement = container.querySelector<HTMLElement>('[data-slot="table"]');
    if (!tableElement) return;
    const hasVerticalScrollbar = container.scrollHeight - container.clientHeight > 1;
    setScrollbarWidth(hasVerticalScrollbar ? container.offsetWidth - container.clientWidth : 0);
    // Compare table width, not container.scrollWidth — sortable header controls have
    // negative margins that bleed past cells and would otherwise be misread as real overflow.
    setHasHorizontalOverflow(tableElement.getBoundingClientRect().width > container.clientWidth);
  };

  // Run after every commit so re-renders driven by data changes paint with the correct
  // scrollbar/overflow state in the same frame, instead of waiting a frame for ResizeObserver.
  useLayoutEffect(update);

  // ResizeObserver covers size changes that don't go through a React re-render
  // (window resize, parent layout shifts).
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;
    const tableElement = container.querySelector('[data-slot="table"]')!;
    const observer = new ResizeObserver(update);
    observer.observe(container);
    observer.observe(tableElement);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      data-slot="data-table"
      className={cn(
        // Core Table normally owns horizontal overflow; this viewport owns both scroll axes.
        'relative flex flex-col gap-2.5 overflow-hidden [&_[data-slot=table-container]]:overflow-visible',
        className,
      )}
      style={
        { ...style, '--data-table-scrollbar-width': `${scrollbarWidth}px` } as React.CSSProperties
      }
      {...props}
    >
      {children}
      <div
        ref={scrollContainerRef}
        data-slot="data-table-viewport"
        className={cn('min-h-0 flex-1 overflow-y-auto', {
          'overflow-x-auto': hasHorizontalOverflow,
          'overflow-x-hidden': !hasHorizontalOverflow,
        })}
      >
        <Table className="tabular-nums">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    scope={header.subHeaders.length ? 'colgroup' : 'col'}
                  >
                    {header.isPlaceholder ? null : (
                      <ColumnHeader column={header.column}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </ColumnHeader>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {renderRows(table, onClickRow, getRowCanClick, renderExpandedRow, getRowClassName)}
          </TableBody>
        </Table>
      </div>
      {!isLoading && isLoadingWithStaleData && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background/60">
          <Loader2Icon className="size-6 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}
